import { useState } from 'react';
import { Check, X, Zap, FlaskConical, Building2, Stethoscope, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';

const NAVY = '#021f30';
const CORAL = '#f93441';

const PLANS = [
  {
    id: 'explorer',
    name: 'Explorer',
    icon: FlaskConical,
    desc: 'For individuals getting started with variant interpretation.',
    price: { monthly: 0, annual: 0 },
    cta: 'Get Started Free',
    ctaStyle: 'outline',
    highlight: false,
    badge: null,
    limits: { calls: '100 / month', batch: '—', support: 'Community', sla: '—' },
    features: [
      { label: 'Web UI access', yes: true },
      { label: 'All annotation fields', yes: true },
      { label: '100 variant lookups / month', yes: true },
      { label: 'API access', yes: false },
      { label: 'Batch queries', yes: false },
      { label: 'PDF report generation', yes: false },
      { label: 'Webhooks', yes: false },
      { label: 'HIPAA-ready handling', yes: false },
      { label: 'SLA guarantee', yes: false },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    icon: Zap,
    desc: 'For small labs and independent bioinformaticians.',
    price: { monthly: 49, annual: 39 },
    cta: 'Start Free Trial',
    ctaStyle: 'outline',
    highlight: false,
    badge: null,
    overage: '$0.03 / extra call',
    limits: { calls: '2,000 / month', batch: '—', support: 'Email', sla: '—' },
    features: [
      { label: 'Web UI access', yes: true },
      { label: 'All annotation fields', yes: true },
      { label: '2,000 API calls / month', yes: true },
      { label: 'REST API + API key', yes: true },
      { label: 'Batch queries', yes: false },
      { label: 'PDF report generation', yes: false },
      { label: 'Webhooks', yes: false },
      { label: 'HIPAA-ready handling', yes: false },
      { label: 'SLA guarantee', yes: false },
    ],
  },
  {
    id: 'research',
    name: 'Research',
    icon: FlaskConical,
    desc: 'For academic labs, core facilities, and growing biotech.',
    price: { monthly: 199, annual: 159 },
    cta: 'Start Free Trial',
    ctaStyle: 'filled',
    highlight: true,
    badge: 'Most Popular',
    overage: '$0.02 / extra call',
    limits: { calls: '20,000 / month', batch: '500 variants / request', support: 'Priority email', sla: '—' },
    features: [
      { label: 'Web UI access', yes: true },
      { label: 'All annotation fields', yes: true },
      { label: '20,000 API calls / month', yes: true },
      { label: 'REST API + API key', yes: true },
      { label: 'Batch queries (500 / request)', yes: true },
      { label: 'PDF report generation', yes: true },
      { label: 'Webhooks', yes: true },
      { label: 'HIPAA-ready handling', yes: false },
      { label: 'SLA guarantee', yes: false },
    ],
  },
  {
    id: 'clinical',
    name: 'Clinical',
    icon: Stethoscope,
    desc: 'For diagnostic labs, hospital systems, and clinical teams.',
    price: { monthly: 799, annual: 639 },
    cta: 'Start Free Trial',
    ctaStyle: 'outline',
    highlight: false,
    badge: null,
    overage: '$0.01 / extra call',
    limits: { calls: '100,000 / month', batch: '500 variants / request', support: 'Dedicated Slack', sla: '99.9%' },
    features: [
      { label: 'Web UI access', yes: true },
      { label: 'All annotation fields', yes: true },
      { label: '100,000 API calls / month', yes: true },
      { label: 'REST API + API key', yes: true },
      { label: 'Batch queries (500 / request)', yes: true },
      { label: 'PDF report generation', yes: true },
      { label: 'Webhooks', yes: true },
      { label: 'HIPAA-ready handling', yes: true },
      { label: '99.9% uptime SLA', yes: true },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    icon: Building2,
    desc: 'Unlimited scale, on-premise options, and white-label support.',
    price: null,
    cta: 'Contact Sales',
    ctaStyle: 'outline',
    highlight: false,
    badge: null,
    limits: { calls: 'Unlimited', batch: 'Custom', support: 'Dedicated account manager', sla: 'Custom SLA' },
    features: [
      { label: 'Everything in Clinical', yes: true },
      { label: 'Unlimited API calls', yes: true },
      { label: 'On-premise / private cloud', yes: true },
      { label: 'White-label option', yes: true },
      { label: 'Custom gene panels & DBs', yes: true },
      { label: 'SSO / SAML integration', yes: true },
      { label: 'Custom ACMG rule config', yes: true },
      { label: 'HIPAA BAA included', yes: true },
      { label: 'Negotiated SLA', yes: true },
    ],
  },
];

const FAQS = [
  {
    q: 'What counts as an API call?',
    a: 'Each variant lookup via the REST API counts as one call. Batch requests count as one call per variant in the batch. Web UI usage does not count toward your API quota.',
  },
  {
    q: 'Can I upgrade or downgrade at any time?',
    a: 'Yes. You can change your plan at any time from your account dashboard. Upgrades take effect immediately; downgrades apply at the start of your next billing cycle.',
  },
  {
    q: 'Is there a free trial for paid plans?',
    a: 'Yes — Starter, Research, and Clinical all include a 14-day free trial. No credit card required to start.',
  },
  {
    q: 'What happens if I exceed my monthly limit?',
      a: "We never cut off your access. Overages are charged per call at the rate listed on each plan. You'll receive an email alert at 80% and 100% of your quota.",
  },
  {
    q: 'Is my genomic data stored or shared?',
    a: 'Variant queries are not stored beyond the session unless you explicitly save them. Clinical and Enterprise plans include a Data Processing Agreement. We never sell or share your data.',
  },
  {
    q: 'How do I get HIPAA compliance?',
    a: 'Clinical and Enterprise plans include HIPAA-ready infrastructure and a Business Associate Agreement (BAA). Contact sales to sign the BAA before processing PHI.',
  },
];

function PlanCard({ plan, annual, onAuthOpen }) {
  const Icon = plan.icon;
  const price = plan.price
    ? annual ? plan.price.annual : plan.price.monthly
    : null;

  return (
    <div
      className="relative flex flex-col rounded-2xl overflow-hidden transition-all"
      style={{
        background: plan.highlight ? NAVY : '#fff',
        border: plan.highlight ? `2px solid ${CORAL}` : '1.5px solid #e2e8f0',
        boxShadow: plan.highlight ? '0 8px 32px rgba(249,52,65,0.18)' : '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      {plan.badge && (
        <div className="absolute top-0 right-0 px-3 py-1 text-xs font-bold text-white rounded-bl-xl"
          style={{ background: CORAL }}>
          {plan.badge}
        </div>
      )}

      <div className="p-6 flex-1 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl"
            style={{ background: plan.highlight ? 'rgba(249,52,65,0.15)' : '#f0f4f8' }}>
            <Icon size={18} style={{ color: CORAL }} />
          </div>
          <div>
            <h3 className="font-bold text-base" style={{ color: plan.highlight ? '#fff' : NAVY }}>
              {plan.name}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: plan.highlight ? 'rgba(255,255,255,0.55)' : '#64748b' }}>
              {plan.desc}
            </p>
          </div>
        </div>

        {/* Price */}
        <div>
          {price !== null ? (
            <div className="flex items-end gap-1">
              <span className="text-4xl font-extrabold" style={{ color: plan.highlight ? '#fff' : NAVY }}>
                ${price}
              </span>
              <span className="text-sm mb-1.5" style={{ color: plan.highlight ? 'rgba(255,255,255,0.5)' : '#94a3b8' }}>
                / mo{annual && price > 0 ? <span className="ml-1 text-green-400 font-semibold">billed annually</span> : ''}
              </span>
            </div>
          ) : (
            <div className="text-3xl font-extrabold" style={{ color: plan.highlight ? '#fff' : NAVY }}>
              Custom
            </div>
          )}
          {plan.overage && (
            <p className="text-xs mt-1" style={{ color: plan.highlight ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
              Overage: {plan.overage}
            </p>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={onAuthOpen}
          className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all hover:brightness-110 active:scale-95"
          style={
            plan.ctaStyle === 'filled'
              ? { background: CORAL, color: '#fff' }
              : plan.highlight
                ? { background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.25)' }
                : { background: 'transparent', color: NAVY, border: `1.5px solid #cbd5e1` }
          }
        >
          {plan.cta}
        </button>

        {/* Divider */}
        <div style={{ borderTop: plan.highlight ? '1px solid rgba(255,255,255,0.1)' : '1px solid #f1f5f9' }} />

        {/* Features */}
        <ul className="flex flex-col gap-2.5">
          {plan.features.map((f) => (
            <li key={f.label} className="flex items-center gap-2.5 text-sm">
              {f.yes
                ? <Check size={14} style={{ color: CORAL, flexShrink: 0 }} />
                : <X size={14} style={{ color: '#cbd5e1', flexShrink: 0 }} />
              }
              <span style={{ color: f.yes ? (plan.highlight ? 'rgba(255,255,255,0.85)' : '#334155') : '#94a3b8' }}>
                {f.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-2">
      {FAQS.map((faq, i) => (
        <div key={i} className="rounded-xl overflow-hidden border"
          style={{ borderColor: '#e2e8f0', background: '#fff' }}>
          <button
            className="w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-sm"
            style={{ color: NAVY }}
            onClick={() => setOpen(open === i ? null : i)}
          >
            {faq.q}
            {open === i ? <ChevronUp size={16} className="shrink-0 text-slate-400" /> : <ChevronDown size={16} className="shrink-0 text-slate-400" />}
          </button>
          {open === i && (
            <div className="px-5 pb-4 text-sm leading-relaxed" style={{ color: '#64748b' }}>
              {faq.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function PricingPage({ onBack, onAuthOpen }) {
  const { user, signOut } = useAuth();
  const [annual, setAnnual] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: '#f0f4f8' }}>

      {/* Header */}
      <header className="sticky top-0 z-40 shadow-sm" style={{ background: NAVY }}>
        <div className="max-w-7xl mx-auto flex items-center gap-4 px-6 py-3">
          <button onClick={onBack} className="shrink-0 hover:opacity-80 transition-opacity" aria-label="Back">
            <Logo size="sm" />
          </button>
          <div className="flex-1" />
          <nav className="flex items-center gap-4 text-sm text-white/60">
            <button onClick={onBack} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <ArrowLeft size={14} /> Back to search
            </button>
            {user ? (
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: CORAL }}>
                  {(user.user_metadata?.full_name || user.email || '?')[0].toUpperCase()}
                </div>
                <button onClick={signOut}
                  className="text-white/50 hover:text-white transition-colors text-xs">Sign out</button>
              </div>
            ) : (
              <button onClick={onAuthOpen}
                className="px-4 py-1.5 rounded-lg border border-white/20 text-white/80 hover:text-white hover:border-white/40 transition-all text-sm">
                Sign in
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-16 flex flex-col gap-16">

        {/* Hero */}
        <div className="text-center flex flex-col items-center gap-4">
          <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest"
            style={{ background: 'rgba(249,52,65,0.1)', color: CORAL }}>
            API Access
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>
            Simple, transparent pricing
          </h1>
          <p className="text-lg max-w-xl" style={{ color: '#64748b' }}>
            Start free. Scale as you grow. All plans include full annotations,
            pathogenicity scores, and ACMG classification.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center gap-3 mt-2 p-1 rounded-xl border"
            style={{ borderColor: '#e2e8f0', background: '#fff' }}>
            <button
              onClick={() => setAnnual(false)}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-all"
              style={{ background: !annual ? NAVY : 'transparent', color: !annual ? '#fff' : '#64748b' }}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all"
              style={{ background: annual ? NAVY : 'transparent', color: annual ? '#fff' : '#64748b' }}
            >
              Annual
              <span className="px-1.5 py-0.5 rounded-md text-xs font-bold"
                style={{ background: annual ? CORAL : '#dcfce7', color: annual ? '#fff' : '#16a34a' }}>
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 items-start">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} annual={annual} onAuthOpen={onAuthOpen} />
          ))}
        </div>

        {/* Comparison table — visible on larger screens */}
        <div className="hidden lg:block">
          <h2 className="text-2xl font-extrabold text-center mb-8" style={{ color: NAVY }}>
            Full feature comparison
          </h2>
          <div className="rounded-2xl overflow-hidden border" style={{ borderColor: '#e2e8f0' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: NAVY }}>
                  <th className="text-left px-5 py-4 text-white/70 font-semibold w-48">Feature</th>
                  {PLANS.map((p) => (
                    <th key={p.id} className="px-4 py-4 text-center font-bold text-white">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Web UI', values: [true, true, true, true, true] },
                  { label: 'API access', values: [false, true, true, true, true] },
                  { label: 'Monthly calls', values: ['100', '2,000', '20,000', '100,000', 'Unlimited'] },
                  { label: 'Batch queries', values: [false, false, '500 / req', '500 / req', 'Custom'] },
                  { label: 'PDF reports', values: [false, false, true, true, true] },
                  { label: 'Webhooks', values: [false, false, true, true, true] },
                  { label: 'HIPAA', values: [false, false, false, true, true] },
                  { label: 'SLA', values: ['—', '—', '—', '99.9%', 'Custom'] },
                  { label: 'Support', values: ['Community', 'Email', 'Priority email', 'Slack', 'Dedicated'] },
                  { label: 'White-label', values: [false, false, false, false, true] },
                  { label: 'On-premise', values: [false, false, false, false, true] },
                ].map((row, i) => (
                  <tr key={row.label} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td className="px-5 py-3 font-medium" style={{ color: '#475569' }}>{row.label}</td>
                    {row.values.map((v, j) => (
                      <td key={j} className="px-4 py-3 text-center">
                        {v === true
                          ? <Check size={15} className="mx-auto" style={{ color: CORAL }} />
                          : v === false
                            ? <X size={15} className="mx-auto text-slate-300" />
                            : <span className="font-medium" style={{ color: NAVY }}>{v}</span>
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="flex flex-col gap-8">
          <h2 className="text-2xl font-extrabold text-center" style={{ color: NAVY }}>
            Frequently asked questions
          </h2>
          <FAQ />
        </div>

        {/* Enterprise CTA banner */}
        <div className="rounded-2xl p-8 sm:p-12 flex flex-col sm:flex-row items-center justify-between gap-6"
          style={{ background: NAVY }}>
          <div>
            <h3 className="text-white text-2xl font-extrabold">Need a custom solution?</h3>
            <p className="text-white/60 mt-1 max-w-md">
              On-premise deployment, custom databases, white-label, HIPAA BAA, or pharma-grade SLAs —
              let's talk.
            </p>
          </div>
          <button
            className="px-8 py-3 rounded-xl font-bold text-white text-sm transition-all hover:brightness-110 active:scale-95 shrink-0"
            style={{ background: CORAL, boxShadow: '0 4px 16px rgba(249,52,65,0.3)' }}
          >
            Contact Sales
          </button>
        </div>

      </main>

      <footer className="text-center py-8 text-xs" style={{ color: '#94a3b8' }}>
        © {new Date().getFullYear()} Haploix · Variant Intelligence ·{' '}
        <span style={{ color: CORAL }}>pricing@haploix.io</span>
      </footer>
    </div>
  );
}
